import {
  action,
  computed,
  IObservableArray,
  makeObservable,
  observable,
} from 'mobx';
import { EventEmitter } from 'events';
import { TypedEmitter } from '../util';
import { ActionMove } from '../model';
import { QueryCache } from 'react-query/core';

interface OptimisticPatchEvents<T> {
  scheduleRemoval: (patch: OptimisticPatch<T>) => unknown;
}

export type OptimisticPatchActionType =
  | 'move'
  | 'createMany'
  | 'updateMany'
  | 'updateAll'
  | 'updateAllConst'
  | 'deleteMany'
  | 'deleteAll';

export interface OptimisticPatchParams<T> {
  readonly patch?: (
    item: T,
    patch: OptimisticPatch<T>,
  ) => Partial<T> | undefined;
  readonly selector: (item: T, patch: OptimisticPatch<T>) => boolean;
  readonly actionType: OptimisticPatchActionType;
  readonly patchedItems: Map<string, T>;
  readonly originalItems: Map<string, T>;
  readonly originalTotal: number;
  readonly keepPosition?: boolean;
  readonly queryNames: string[];
  readonly queryCache: QueryCache;
  readonly patches: IObservableArray<OptimisticPatch<T>>;
  readonly move?: ActionMove<T>;
  readonly collectionSnapshot?: { [key: string]: string | number | undefined };
  readonly afterItemKey?: string;
}

export class OptimisticPatch<T> {
  readonly patch;
  readonly selector;
  readonly actionType;
  readonly patchedItems;
  readonly originalItems;
  readonly originalTotal;
  readonly keepPosition;
  readonly queryNames;
  readonly queryCache;
  readonly patches;
  readonly move;
  readonly createdAt = Date.now();
  readonly collectionSnapshot;
  readonly afterItemKey;

  isStale: boolean = false;

  readonly events = new EventEmitter() as TypedEmitter<
    OptimisticPatchEvents<T>
  >;

  constructor(params: OptimisticPatchParams<T>) {
    this.patch = (item: T) => params.patch?.(item, this);
    this.selector = (item: T) => params.selector(item, this);
    this.actionType = params.actionType;
    this.keepPosition = params.keepPosition;
    this.queryNames = params.queryNames;
    this.queryCache = params.queryCache;
    this.patches = params.patches;
    this.move = params.move;
    this.collectionSnapshot = params.collectionSnapshot;
    this.afterItemKey = params.afterItemKey;

    this.patchedItems = observable.map<string, T>(params.patchedItems, {
      deep: false,
    });

    this.originalItems = observable.map<string, T>(params.originalItems, {
      deep: false,
    });

    this.originalTotal = params.originalTotal;

    makeObservable(this, {
      patchedItemsEntries: computed,
      isStale: observable.ref,
      onEventuallyUpdated: action,
    });
  }

  get patchedItemsEntries() {
    return Array.from(this.patchedItems.entries());
  }

  onEventuallyUpdated = () => {
    const { events, queryCache, queryNames, patches } = this;
    this.isStale = true;
    if (!events.listeners('scheduleRemoval').length) {
      patches.remove(this);
      queryNames.forEach((queryName) => {
        queryCache
          .findAll({ queryKey: [queryName] })
          .forEach((q) => q.invalidate());
      });
    } else {
      events.emit('scheduleRemoval', this);
    }
  };
}
